/**
 * useAccountProfile — ViewModel for /account/profile.
 *
 * Per SOP 02 §"MVVM Pattern" + SOP 15 Ch.20 (SOLID — S/D). Owns the
 * customer-edit lifecycle. The customer itself is hydrated by
 * AuthContext at mount (cookie probe), so `isLoading` here mirrors
 * AuthContext's hydration state and `reload` re-runs that probe via a
 * fresh GET /account/me — convenient for the rare "form went stale"
 * case without forcing a page reload.
 */

import { useCallback, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { APIROUTES } from '../../constants/routes.constants'
import type { User } from '../../models/user.model'
import { mutateJson } from '../../utils/api/mutateJson'
import { parseApiError } from '../../utils/api/parseApiError'

interface ApiOkBody<T> {
  readonly success: true
  readonly data: T
}

export interface UpdateProfileInput {
  readonly firstName: string | null
  readonly lastName: string | null
  readonly phone: string | null
  readonly acceptsMarketing: boolean
}

export interface UseAccountProfileReturn {
  readonly user: User | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly update: (
    patch: UpdateProfileInput
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly reload: () => Promise<void>
}

export function useAccountProfile(): UseAccountProfileReturn {
  const { user, isLoading, authedFetch, setUserLocal } = useAuth()
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async (): Promise<void> => {
    setError(null)
    try {
      const res = await authedFetch(APIROUTES.ACCOUNTME)
      if (!res.ok) {
        setError((await parseApiError(res)).message)
        return
      }
      const body = (await res.json()) as ApiOkBody<User>
      setUserLocal(body.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not refresh profile')
    }
  }, [authedFetch, setUserLocal])

  const update = useCallback(
    async (patch: UpdateProfileInput) => {
      const r = await mutateJson<User>(
        authedFetch,
        APIROUTES.ACCOUNTME,
        'PATCH',
        patch
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      setUserLocal(r.data)
      return { ok: true as const }
    },
    [authedFetch, setUserLocal]
  )

  return { user, isLoading, error, update, reload }
}
